import {
  DASHBOARD_COLS,
  DashboardConfig,
  DashboardLayoutItem,
  WIDGET_SIZES,
  WidgetInstance,
  WidgetKind,
  WidgetSettings,
  isWidgetKind,
} from './Dashboard.Types';

export const DASHBOARD_CONFIG_VERSION = 1;

//#region Panel por defecto

/** Panel inicial de un usuario administrativo: resumen de liquidaciones, pendientes, contratos fijados, importaciones, vencimientos y accesos. */
export const defaultDashboardConfig = (): DashboardConfig => ({
  version: DASHBOARD_CONFIG_VERSION,
  widgets: [
    { id: 'settlementsSummary', kind: 'settlementsSummary' },
    { id: 'pendingSettlements', kind: 'pendingSettlements' },
    { id: 'pinnedContracts', kind: 'pinnedContracts', settings: { contractIds: [] } },
    { id: 'importJobs', kind: 'importJobs' },
    { id: 'expiringContracts', kind: 'expiringContracts', settings: { days: 60 } },
    { id: 'quickLinks', kind: 'quickLinks' },
  ],
  layout: [
    { i: 'settlementsSummary', x: 0, y: 0, w: 12, h: 3 },
    { i: 'pendingSettlements', x: 0, y: 3, w: 7, h: 7 },
    { i: 'pinnedContracts', x: 7, y: 3, w: 5, h: 7 },
    { i: 'importJobs', x: 0, y: 10, w: 6, h: 6 },
    { i: 'expiringContracts', x: 6, y: 10, w: 6, h: 6 },
    { i: 'quickLinks', x: 0, y: 16, w: 12, h: 3 },
  ],
});

//#endregion

//#region Normalización

const toInt = (value: unknown, fallback: number): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** Ajusta un ítem del layout a los límites del widget y de la grilla. */
export const clampLayoutItem = (item: DashboardLayoutItem, kind: WidgetKind): DashboardLayoutItem => {
  const size = WIDGET_SIZES[kind];
  const w = clamp(toInt(item.w, size.w), size.minW, DASHBOARD_COLS);
  const h = clamp(toInt(item.h, size.h), size.minH, size.maxH ?? Number.MAX_SAFE_INTEGER);
  const x = clamp(toInt(item.x, 0), 0, DASHBOARD_COLS - w);
  const y = Math.max(0, toInt(item.y, 0));
  return { i: item.i, x, y, w, h };
};

/**
 * Valida lo guardado (puede venir de una versión vieja o estar roto): descarta widgets desconocidos,
 * completa el layout de los que no lo tengan y acota tamaños. Devuelve `undefined` si no sirve.
 */
export const normalizeDashboardConfig = (raw: unknown): DashboardConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Partial<DashboardConfig>;
  if (!Array.isArray(source.widgets)) return undefined;

  const seen = new Set<string>();
  const widgets: WidgetInstance[] = [];
  source.widgets.forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') return;
    const { id, kind, settings } = candidate as Partial<WidgetInstance>;
    if (typeof id !== 'string' || !id.trim() || seen.has(id) || !isWidgetKind(kind)) return;
    seen.add(id);
    const widget: WidgetInstance = { id, kind };
    if (settings && typeof settings === 'object') widget.settings = { ...(settings as WidgetSettings) };
    widgets.push(widget);
  });
  if (widgets.length === 0) return undefined;

  const storedLayout = Array.isArray(source.layout) ? (source.layout as Partial<DashboardLayoutItem>[]) : [];
  const layout: DashboardLayoutItem[] = widgets.map((widget) => {
    const stored = storedLayout.find((item) => item && item.i === widget.id);
    const size = WIDGET_SIZES[widget.kind];
    return clampLayoutItem(
      { i: widget.id, x: stored?.x ?? 0, y: stored?.y ?? nextFreeRow(layoutSoFar(storedLayout)), w: stored?.w ?? size.w, h: stored?.h ?? size.h },
      widget.kind
    );
  });

  return { version: DASHBOARD_CONFIG_VERSION, widgets, layout };
};

const layoutSoFar = (items: Partial<DashboardLayoutItem>[]): DashboardLayoutItem[] =>
  items
    .filter((item): item is DashboardLayoutItem => !!item && typeof item.i === 'string')
    .map((item) => ({ i: item.i, x: toInt(item.x, 0), y: toInt(item.y, 0), w: toInt(item.w, 1), h: toInt(item.h, 1) }));

/** Primera fila libre debajo de todo lo que hay. */
export const nextFreeRow = (layout: DashboardLayoutItem[]): number =>
  layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

//#endregion

//#region Operaciones

export const newWidgetId = (kind: WidgetKind, now: number = Date.now()): string => `${kind}-${now.toString(36)}`;

export const defaultWidgetSettings = (kind: WidgetKind): WidgetSettings | undefined => {
  switch (kind) {
    case 'pinnedContracts':
      return { contractIds: [] };
    case 'expiringContracts':
      return { days: 60 };
    default:
      return undefined;
  }
};

/** Agrega un widget al final del panel, a la izquierda, con su tamaño por defecto. */
export const addWidget = (config: DashboardConfig, kind: WidgetKind, id: string = newWidgetId(kind)): DashboardConfig => {
  if (config.widgets.some((widget) => widget.id === id)) return config;
  const size = WIDGET_SIZES[kind];
  const settings = defaultWidgetSettings(kind);
  const widget: WidgetInstance = settings ? { id, kind, settings } : { id, kind };
  return {
    ...config,
    widgets: [...config.widgets, widget],
    layout: [...config.layout, { i: id, x: 0, y: nextFreeRow(config.layout), w: size.w, h: size.h }],
  };
};

export const removeWidget = (config: DashboardConfig, id: string): DashboardConfig => ({
  ...config,
  widgets: config.widgets.filter((widget) => widget.id !== id),
  layout: config.layout.filter((item) => item.i !== id),
});

export const updateWidgetSettings = (config: DashboardConfig, id: string, settings: WidgetSettings): DashboardConfig => ({
  ...config,
  widgets: config.widgets.map((widget) => (widget.id === id ? { ...widget, settings: { ...(widget.settings ?? {}), ...settings } } : widget)),
});

/** Toma el layout que devuelve la grilla (puede traer ítems de más o de menos) y lo guarda acotado. */
export const applyLayout = (config: DashboardConfig, layout: ReadonlyArray<Pick<DashboardLayoutItem, 'i' | 'x' | 'y' | 'w' | 'h'>>): DashboardConfig => {
  const next = config.widgets.map((widget) => {
    const item = layout.find((candidate) => candidate.i === widget.id) ?? config.layout.find((candidate) => candidate.i === widget.id);
    return clampLayoutItem(item ? { i: widget.id, x: item.x, y: item.y, w: item.w, h: item.h } : { i: widget.id, x: 0, y: nextFreeRow(config.layout), ...WIDGET_SIZES[widget.kind] }, widget.kind);
  });
  return layoutsEqual(config.layout, next) ? config : { ...config, layout: next };
};

export const layoutsEqual = (a: DashboardLayoutItem[], b: DashboardLayoutItem[]): boolean =>
  a.length === b.length &&
  a.every((item) => {
    const other = b.find((candidate) => candidate.i === item.i);
    return !!other && other.x === item.x && other.y === item.y && other.w === item.w && other.h === item.h;
  });

/** El layout tal como lo espera react-grid-layout, con los límites de cada tipo. */
export const toGridLayout = (config: DashboardConfig) =>
  config.layout.map((item) => {
    const widget = config.widgets.find((candidate) => candidate.id === item.i);
    const size = widget ? WIDGET_SIZES[widget.kind] : undefined;
    return { ...item, minW: size?.minW, minH: size?.minH, maxH: size?.maxH };
  });

//#endregion

//#region Persistencia por usuario

const KEY_PREFIX = 'ichtys.dashboard';
const ANONYMOUS = 'anonymous';
const memoryStore = new Map<string, DashboardConfig>();

export const dashboardStorageKey = (userId?: string | null): string =>
  `${KEY_PREFIX}.${userId && String(userId).trim() ? String(userId).trim() : ANONYMOUS}`;

export const readDashboardConfig = (userId?: string | null): DashboardConfig => {
  const key = dashboardStorageKey(userId);
  try {
    const stored = window.localStorage.getItem(key);
    if (stored) {
      const normalized = normalizeDashboardConfig(JSON.parse(stored));
      if (normalized) return normalized;
    }
  } catch {
    // storage inaccesible o JSON roto: se usa la copia en memoria o el panel por defecto
  }
  return memoryStore.get(key) ?? defaultDashboardConfig();
};

export const writeDashboardConfig = (userId: string | null | undefined, config: DashboardConfig): void => {
  const key = dashboardStorageKey(userId);
  memoryStore.set(key, config);
  try {
    window.localStorage.setItem(key, JSON.stringify(config));
  } catch {
    // storage inaccesible: queda la copia en memoria
  }
};

export const clearDashboardConfig = (userId?: string | null): void => {
  const key = dashboardStorageKey(userId);
  memoryStore.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // nada que limpiar
  }
};

/** Sólo para tests. */
export const clearDashboardMemoryStore = (): void => memoryStore.clear();

//#endregion
